import React, { useState, useEffect } from 'react';
import './styles.scss';
import '../FormElements/common.scss';
import {
  getCountriesSorted,
  getDefaultCountry,
  Country,
} from './country-codes';
import { parsePhoneNumber } from '@/utils/phone-utils';
import { useCustomComponent } from '@/hooks';
import Components from './components';

export interface PhoneInputProps {
  id?: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const PhoneInputComponent: React.FC<PhoneInputProps> = ({
  id,
  name,
  placeholder = 'Phone number',
  required = false,
  disabled = false,
  value = '',
  onChange,
  onBlur,
  onFocus,
  className = '',
  ...ariaProps
}) => {
  const [selectedCountry, setSelectedCountry] =
    useState<Country>(getDefaultCountry());
  const [phoneNumber, setPhoneNumber] = useState('');

  const countries = getCountriesSorted();

  // Initialize phone number from value prop.
  // Only attempt to parse as an international number when the value starts
  // with '+' (i.e. the E.164 string written back on blur). During typing the
  // parent echoes back the raw national number without '+', which must not be
  // re-parsed or the dial-code prefix gets eaten as country digits.
  useEffect(() => {
    if (value) {
      if (value.startsWith('+')) {
        try {
          const parsed = parsePhoneNumber(value);
          if (parsed) {
            const country = countries.find(c => c.code === parsed.country);
            if (country) {
              setSelectedCountry(country);
            }
            setPhoneNumber(parsed.nationalNumber);
          } else {
            // Parsing failed (e.g. very short number). Strip the currently
            // selected country's dial code so we don't dump the full E.164
            // string into the national-number input box.
            const dialDigits = selectedCountry.dialCode.replace(/\D/g, '');
            const valueDigits = value.replace(/\D/g, '');
            if (valueDigits.startsWith(dialDigits)) {
              setPhoneNumber(valueDigits.slice(dialDigits.length));
            }
            // If we can't strip the dial code either, leave the input unchanged.
          }
        } catch {
          // Leave the input unchanged on unexpected parse errors.
        }
      } else {
        // Raw national number (mid-typing) — display as-is
        setPhoneNumber(value);
      }
    } else {
      // Clear internal state when value prop is empty
      setPhoneNumber('');
    }
  }, [value]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip everything that isn't a digit (type="tel" only enforces numeric
    // keyboard on mobile; desktop browsers allow any character).
    const newNumber = e.target.value.replace(/\D/g, '');
    setPhoneNumber(newNumber);
    // Only send the raw phone number during typing, no dial code
    onChange?.(newNumber);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    // When country changes, update with full number if we have a phone number
    if (phoneNumber.trim()) {
      const fullNumber = getFullPhoneNumber(phoneNumber, country);
      onChange?.(fullNumber);
    }
  };

  // Helper function to get full international number
  // Always returns E.164 format (e.g., +11234567890) for proper validation
  const getFullPhoneNumber = (number: string, country: Country) => {
    if (!number.trim()) {
      return '';
    }

    try {
      const fullNumber = `${country.dialCode}${number}`;
      const parsed = parsePhoneNumber(fullNumber);
      // parsed.number returns E.164 format (e.g., +11234567890)
      return parsed ? parsed.number : fullNumber;
    } catch {
      // If parsing fails, return the concatenated string
      // This will be validated by isValidPhoneNumber which can handle various formats
      return `${country.dialCode}${number}`;
    }
  };

  const handleInputFocus = () => {
    onFocus?.();
  };

  const handleInputBlur = () => {
    // On blur, send the full phone number with dial code for validation
    if (phoneNumber.trim()) {
      const fullNumber = getFullPhoneNumber(phoneNumber, selectedCountry);
      onChange?.(fullNumber);
    } else {
      // Clear the value when field is empty
      onChange?.('');
    }
    onBlur?.();
  };

  return (
    <Components.Container className={className}>
      {/* Country Selector */}
      {/* Country Selector */}
      <Components.FilterableCountryDropdown
        countries={countries}
        value={selectedCountry.code}
        onChange={handleCountrySelect}
        disabled={disabled}
        aria-label="Select country code"
      />

      {/* Phone Number Input */}
      <Components.NumberInput
        type="tel"
        id={id}
        name={name}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        maxLength={15 - selectedCountry.dialCode.replace(/\D/g, '').length}
        className="peptalk-form-element peptalk-phone-number-input"
        {...ariaProps}
      />
    </Components.Container>
  );
};

export const PhoneInput: React.FC<PhoneInputProps> = (
  props: PhoneInputProps
) => {
  const Component = useCustomComponent({
    componentName: 'PhoneInput',
    Component: PhoneInputComponent,
  });

  return <Component {...props} />;
};
